import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Sparkles, Zap, Target, Users, Plug } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useSiteI18n } from '../SiteI18n';
import { PILLARS } from '../data/pillarsContent';

const sectionAnim = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.55 },
};

export const PillarShowcase: React.FC = () => {
  const { lang, t } = useSiteI18n();

  return (
    <div className="space-y-32 md:space-y-44 pb-20">
      {PILLARS.map((p, idx) => {
        const c = p[lang];
        const Icon = p.icon;
        const reverse = idx % 2 === 1;
        return (
          <section key={p.slug} id={p.slug} className="relative scroll-mt-24">
            {/* ambient glow */}
            <div
              aria-hidden
              className={`absolute -top-32 ${reverse ? 'right-[-10%]' : 'left-[-10%]'} w-[700px] h-[700px] rounded-full blur-[140px] site-aurora ${p.glowClass}`}
            />

            <div className="container mx-auto px-4 lg:px-6 relative">
              <div className={`grid lg:grid-cols-2 gap-12 lg:gap-16 items-center ${reverse ? 'lg:[&>*:first-child]:order-2' : ''}`}>
                {/* COPY */}
                <motion.div {...sectionAnim}>
                  <div className="flex items-center gap-2 mb-5">
                    <div className={`h-11 w-11 rounded-2xl border bg-gradient-to-br ${p.gradientClass} ${p.accentClass} grid place-items-center backdrop-blur-md`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <Badge variant="outline" className={`${p.accentClass} bg-card/40 backdrop-blur-md`}>
                      {c.tag}
                    </Badge>
                  </div>

                  <h2 className="text-3xl md:text-5xl font-black tracking-tight mb-3 leading-[1.05]">
                    <span className="bg-gradient-to-br from-foreground via-foreground to-foreground/50 bg-clip-text text-transparent">
                      {c.title}
                    </span>
                  </h2>
                  <p className="text-lg md:text-xl font-semibold mb-4 site-shimmer-text inline-block">
                    {c.headline}
                  </p>
                  <p className="text-muted-foreground leading-relaxed text-lg md:text-xl max-w-xl">
                    {c.subtitle}
                  </p>

                  {/* KPIs */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-8">
                    {c.kpis.map((k, i) => (
                      <motion.div
                        key={k.l}
                        initial={{ opacity: 0, y: 14 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: i * 0.06 }}
                        className="rounded-xl border border-border/60 bg-card/50 backdrop-blur-md p-3"
                      >
                        <div className={`text-2xl md:text-3xl font-black ${p.accentClass} drop-shadow-[0_0_12px_currentColor]`}>
                          {k.v}
                        </div>
                        <div className="text-sm text-muted-foreground mt-0.5">{k.l}</div>
                      </motion.div>
                    ))}
                  </div>

                  {/* Modules chips */}
                  <div className="mt-7 flex flex-wrap gap-1.5">
                    {c.modules.map((m) => (
                      <span
                        key={m}
                        className="text-sm px-2.5 py-1 rounded-full border border-border/60 bg-card/40 text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors"
                      >
                        {m}
                      </span>
                    ))}
                  </div>

                  {p.slug === 'care' && (
                    <div className="mt-7">
                      <Button asChild size="lg" className={`bg-gradient-to-br ${p.gradientClass} text-foreground border border-border/60 hover:opacity-90`}>
                        <Link to="/pacient-x-programare">
                          {lang === 'ro' ? 'Consultație' : 'Book a consultation'}
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Link>
                      </Button>
                    </div>
                  )}

                </motion.div>

                {/* IMAGE / CARD */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.7 }}
                  className="relative"
                >
                  <div className={`absolute -inset-6 bg-gradient-to-br ${p.gradientClass} rounded-[2rem] blur-2xl opacity-80`} />
                  <div className="relative rounded-[1.5rem] overflow-hidden border border-border/60 shadow-2xl">
                    <img
                      src={p.image}
                      alt={c.title}
                      width={1200}
                      height={900}
                      loading="lazy"
                      className="w-full h-auto object-cover hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-tr from-background/40 via-transparent to-transparent" />
                    {/* floating proof badge */}
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 }}
                      className="absolute bottom-4 left-4 right-4 rounded-xl border border-border/60 bg-background/85 backdrop-blur-xl p-3 flex items-start gap-2.5 shadow-xl"
                    >
                      <Sparkles className={`h-4 w-4 mt-0.5 shrink-0 ${p.accentClass.split(' ')[0]}`} />
                      <p className="text-xs text-foreground/80 leading-relaxed">{c.proof}</p>
                    </motion.div>
                  </div>
                </motion.div>
              </div>

              {/* CAPABILITIES grid */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.6 }}
                className="mt-16"
              >
                <div className="flex items-center gap-2 mb-5">
                  <Zap className={`h-4 w-4 ${p.accentClass.split(' ')[0]}`} />
                  <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
                    {lang === 'ro' ? 'Capabilități cheie' : 'Key capabilities'}
                  </h3>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {c.capabilities.map((cap, i) => (
                    <motion.div
                      key={cap.t}
                      initial={{ opacity: 0, y: 16 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.04 }}
                      className="group relative rounded-2xl border border-border/60 bg-card/50 backdrop-blur-md p-5 hover:border-primary/40 hover:-translate-y-1 transition-all overflow-hidden"
                    >
                      <div className={`absolute -top-12 -right-12 w-32 h-32 rounded-full ${p.glowClass} opacity-0 group-hover:opacity-60 blur-2xl transition-opacity`} />
                      <div className="relative">
                        <div className="flex items-start gap-2 mb-2">
                          <Check className={`h-4 w-4 mt-0.5 ${p.accentClass.split(' ')[0]} shrink-0`} />
                          <h4 className="font-bold text-sm">{cap.t}</h4>
                        </div>
                        <p className="text-sm text-muted-foreground leading-relaxed pl-6">{cap.d}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* USE CASES + INTEGRATIONS + AUDIENCE */}
              <div className="mt-12 grid lg:grid-cols-3 gap-5">
                {/* Use cases */}
                <motion.div {...sectionAnim} className="rounded-2xl border border-border/60 bg-card/40 backdrop-blur-md p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Target className={`h-4 w-4 ${p.accentClass.split(' ')[0]}`} />
                    <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                      {lang === 'ro' ? 'Cazuri de utilizare' : 'Use cases'}
                    </h4>
                  </div>
                  <ul className="space-y-3">
                    {c.useCases.map((u) => (
                      <li key={u.t}>
                        <div className="font-semibold text-sm">{u.t}</div>
                        <div className="text-xs text-muted-foreground leading-relaxed mt-0.5">{u.d}</div>
                      </li>
                    ))}
                  </ul>
                </motion.div>

                {/* Audience */}
                <motion.div {...sectionAnim} transition={{ ...sectionAnim.transition, delay: 0.1 }} className="rounded-2xl border border-border/60 bg-card/40 backdrop-blur-md p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Users className={`h-4 w-4 ${p.accentClass.split(' ')[0]}`} />
                    <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                      {lang === 'ro' ? 'Pentru cine' : 'Who it’s for'}
                    </h4>
                  </div>
                  <ul className="space-y-1.5">
                    {c.audience.map((a) => (
                      <li key={a} className="text-sm text-foreground/80 flex items-center gap-2">
                        <span className={`h-1.5 w-1.5 rounded-full bg-current ${p.accentClass.split(' ')[0]}`} />
                        {a}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              </div>
            </div>
          </section>
        );
      })}
    </div>
  );
};
